import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { t } from 'i18next';

import {
  FORM_TABLE_COLUMN_NAME,
  FORM_TABLE_FIRST_COLUMN_NAME,
} from 'components/_basic/Form/FormTableComponent/constants';

import { getAdditionalColumnProperties } from '../../helpers';

export const MAX_FIELD_LENGTH = 5;

export const COST_OF_EACH_UNIT_FORM_NAMES = {
  CONST_OF_EACH_UNIT_CURRENCY: 'costOfEachUnitCurrency',
  COST_OF_EACH_UNIT_TABLE: 'costOfEachUnitTable',
  COST_OF_EACH_UNIT_TABLE_TOTAL: 'totalCostOfEachUnitTable',
  COST_OF_EACH_UNIT_PRODUCTS_NAMES: 'productsName',
} as const;

// ---- COST_OF_EACH_UNIT_TABLE ----

export const useGetParameterColumnDefaultValues = () => {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => [
      'Salaries for production staff',
      'Benefits and payroll taxes for production staff',
      t('pdfPages.mappings.financialPlan.costOfEachUnitForm.directRawMaterials'),
      t('pdfPages.mappings.financialPlan.costOfEachUnitForm.utilitiesForProductionFacilities'),
      t('pdfPages.mappings.financialPlan.costOfEachUnitForm.subcontractorServices'),
      t('pdfPages.mappings.financialPlan.costOfEachUnitForm.otherProcurementCosts'),
      t('pdfPages.mappings.financialPlan.costOfEachUnitForm.packaging'),
      t('pdfPages.mappings.financialPlan.costOfEachUnitForm.transportationAndLogistics'),
      t('pdfPages.mappings.financialPlan.costOfEachUnitForm.inventoryCosts'),
      t('pdfPages.mappings.financialPlan.costOfEachUnitForm.qualityControlAndTesting'),
      t('pdfPages.mappings.financialPlan.costOfEachUnitForm.other'),
    ],
    [t, i18n],
  );
};

export const parameterColumnDefaultValues = [
  'Salaries for production staff',
  'Benefits and payroll taxes for production staff',
  t('pdfPages.mappings.financialPlan.costOfEachUnitForm.directRawMaterials'),
  t('pdfPages.mappings.financialPlan.costOfEachUnitForm.utilitiesForProductionFacilities'),
  t('pdfPages.mappings.financialPlan.costOfEachUnitForm.subcontractorServices'),
  t('pdfPages.mappings.financialPlan.costOfEachUnitForm.otherProcurementCosts'),
  t('pdfPages.mappings.financialPlan.costOfEachUnitForm.packaging'),
  t('pdfPages.mappings.financialPlan.costOfEachUnitForm.transportationAndLogistics'),
  t('pdfPages.mappings.financialPlan.costOfEachUnitForm.inventoryCosts'),
  t('pdfPages.mappings.financialPlan.costOfEachUnitForm.qualityControlAndTesting'),
  t('pdfPages.mappings.financialPlan.costOfEachUnitForm.other'),
];

const additionalProperties: Record<string, string> = getAdditionalColumnProperties(
  FORM_TABLE_COLUMN_NAME,
  MAX_FIELD_LENGTH,
);

export const defaultCostOfEachUnitItem = { [FORM_TABLE_FIRST_COLUMN_NAME]: '', ...additionalProperties };
export const defaultProductOfEachUnitTable = { column0: '' };

export const getDefaultCostOfEachUnitRows = (defaultValues: string[]) =>
  defaultValues.map((item) => {
    return { [FORM_TABLE_FIRST_COLUMN_NAME]: item, column0: '' };
  });

//  ---- COMMON ----
export interface ICostOfEachUnitValuesType {
  [COST_OF_EACH_UNIT_FORM_NAMES.CONST_OF_EACH_UNIT_CURRENCY]?: string;
  [COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_TABLE]?: Record<string, string | number | undefined>[];
  [COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_TABLE_TOTAL]?: Record<string, string | number | undefined>;
  [COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_PRODUCTS_NAMES]?: Record<string, string | number | undefined>;
}

export const useGetDefaultCostOfEachUnitRows = () => {
  const defaultValues = useGetParameterColumnDefaultValues();

  return useMemo(() => getDefaultCostOfEachUnitRows(defaultValues), [defaultValues]);
};

export const useGetCostOfEachUnitInitialValues = (): ICostOfEachUnitValuesType => {
  const defaultRows = useGetDefaultCostOfEachUnitRows();

  return useMemo(
    () => ({
      [COST_OF_EACH_UNIT_FORM_NAMES.CONST_OF_EACH_UNIT_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
      [COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_TABLE]: defaultRows,
      [COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_TABLE_TOTAL]: defaultCostOfEachUnitItem,
      [COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_PRODUCTS_NAMES]: defaultProductOfEachUnitTable,
    }),
    [defaultRows],
  );
};

export const costFoEachUnitInitialValues = {
  [COST_OF_EACH_UNIT_FORM_NAMES.CONST_OF_EACH_UNIT_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
  [COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_TABLE]: getDefaultCostOfEachUnitRows(parameterColumnDefaultValues),
  [COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_TABLE_TOTAL]: defaultCostOfEachUnitItem,
  [COST_OF_EACH_UNIT_FORM_NAMES.COST_OF_EACH_UNIT_PRODUCTS_NAMES]: defaultProductOfEachUnitTable,
};
